import { useCallback } from 'react';
import { t } from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import ClickCoin from '@/enterprise/assets/clickcoin.svg';
import workingIdea from '@/enterprise/assets/workingIdea.svg';
import useCreateBuyerLendingRequest from '@/enterprise/data/useCreateBuyerLendingRequest';
import { useReporting } from '@/reporting';
import useUserDetails from '@/shared/data/useUserDetails';
import { validateBuyerLendingRequest } from '../utils';
import ContactStep from './ContactStep';
import GoalsStep from './GoalsStep';
import { useLendingModalContext } from './LendingModalContext';
import ScheduleStep from './ScheduleStep';
import { LendingFormConfig } from './types';

// Expandable type set post mvp. i.e. widget, marketing page, etc
export type LendingModalReferrer = 'inline-ad';

const LendingModal = ({ isOpen, setOpenModal }: { isOpen: boolean; setOpenModal: (open: boolean) => void }) => {
  const { modalStep, setModalStep, hasSelectedDateAndTime, setHasSelectedDateAndTime, referrer } =
    useLendingModalContext();
  const { data: userData } = useUserDetails(({ me }) => me);
  const { trackEnterpriseEvent } = useReporting();
  const showSnackbar = useSnackbar();
  const { mutateAsync: createBuyerLendingRequest } = useCreateBuyerLendingRequest();

  const getDefaultFormValues = useCallback(() => {
    const name = `${userData?.personalInfo?.firstName} ${userData?.personalInfo?.lastName}`;
    const email = userData?.emailAddress ?? '';
    const phoneNumber =
      (userData?.personalInfo?.primaryPhoneNumber || userData?.personalInfo?.secondaryPhoneNumber) ?? '';
    const jobTitle = userData?.personalInfo?.role ?? '';

    return {
      contact: {
        name,
        email,
        phoneNumber,
        jobTitle,
      },
      goals: {
        products: [],
        priorities: [],
      },
    } satisfies LendingFormConfig;
  }, [userData]);

  const form = useForm<LendingFormConfig>({
    defaultValues: getDefaultFormValues(),
  });

  const { reset, handleSubmit } = form;

  const resetForm = useCallback(() => {
    reset(getDefaultFormValues());
    setModalStep('contact');
  }, [getDefaultFormValues, reset, setModalStep]);

  const onNext = () => {
    if (modalStep === 'contact') {
      setModalStep('goals');
      trackEnterpriseEvent('lending::modal::contact::completed', { referrer });
    } else if (modalStep === 'goals') {
      setModalStep('schedule');
      trackEnterpriseEvent('lending::modal::goals::completed', { referrer });
    }
  };

  const onBack = () => {
    if (modalStep === 'goals') {
      setModalStep('contact');
    } else if (modalStep === 'schedule') {
      setModalStep('goals');
      setHasSelectedDateAndTime(false);
    }
  };

  const onClose = () => {
    setOpenModal(false);
    // Still create the Salesforce opportunity even if the user doesn't schedule a meeting
    // Indicate in opportunity if the meeting was scheduled
    let appointmentScheduled = false;
    if (modalStep === 'success') {
      showSnackbar({ message: 'C2FO Lending Connections meeting scheduled' });
      appointmentScheduled = true;
    }

    const { contact, goals } = form.getValues();
    const isFormValid = validateBuyerLendingRequest(contact, goals);
    if (isFormValid) {
      createBuyerLendingRequest({
        company: contact.company,
        annualRevenue: parseInt(contact.annualRevenue ?? '0'),
        customers: contact.customers,
        capitalAmount: goals.capitalAmount,
        products: goals.products,
        priorities: goals.priorities,
        existingFinancing: goals.existingFinancing!, // validated by form
        appointmentScheduled,
      });
    }

    resetForm();
    trackEnterpriseEvent('lending::modal::closed', { referrer, modalStep, appointmentScheduled });
  };

  const SuccessStep = () => (
    <div className="flex flex-col items-center">
      <p className="text-2xl font-bold">Your Lending Connections meeting has been scheduled!</p>
      <img src={workingIdea} alt="Working idea graphic" className="p-4" />
      <p>A confirmation for your meeting will be sent to your email.</p>
    </div>
  );

  let subtitle = '';
  if (modalStep === 'contact' || modalStep === 'goals') {
    subtitle = 'Meeting Details';
  } else if (modalStep === 'schedule') {
    subtitle = hasSelectedDateAndTime ? 'Review and Schedule' : 'Set Date and Time';
  }

  return (
    <Modal open={isOpen} onClose={onClose} className="sm:w-[700px]" disableOutsideClickClose>
      <ModalTitleInverse>
        {modalStep !== 'success' ? (
          <div className="flex flex-col">
            <span>Lending Connections Contact</span>
            <span className="text-sm font-normal text-text-secondary">{subtitle}</span>
          </div>
        ) : null}
      </ModalTitleInverse>
      <ModalContent>
        {(modalStep === 'contact' || modalStep === 'goals') && (
          <div className="flex rounded-lg bg-gray-50 p-4">
            <div className="flex items-center p-4">
              <img src={ClickCoin} alt="ClickCoinIcon" />
            </div>
            <div className="flex flex-col gap-4">
              <div>
                Through C2FO Lending Connections, our dedicated advisors work with you to give your company access to
                the financing you need to ensure your business thrives.
              </div>
              <div className="font-semibold">
                {modalStep === 'contact' && 'Confirm your contact and business details to prepare our team.'}
                {modalStep === 'goals' && 'Help make our meeting productive by sharing your goals.'}
              </div>
            </div>
          </div>
        )}
        <FormProvider {...form}>
          <form>
            {modalStep === 'contact' && <ContactStep />}
            {modalStep === 'goals' && <GoalsStep />}
            {modalStep === 'schedule' && <ScheduleStep />}
          </form>
        </FormProvider>
        {modalStep === 'success' && <SuccessStep />}
      </ModalContent>
      <ModalActions>
        {(modalStep === 'goals' || modalStep === 'schedule') && (
          <Button variant="secondary" onClick={onBack}>
            {modalStep === 'goals' ? t('core.back') : 'Back to Details'}
          </Button>
        )}
        {(modalStep === 'contact' || modalStep === 'success') && (
          <Button variant="secondary" onClick={onClose}>
            {modalStep === 'contact' ? t('core.cancel') : t('core.close')}
          </Button>
        )}
        {(modalStep === 'contact' || modalStep === 'goals') && (
          <Button variant="primary" onClick={handleSubmit(onNext)} className="sm:min-w-[100px]">
            {modalStep === 'contact' ? 'Next' : 'Next: Schedule Meeting'}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export default LendingModal;
